'use client';

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useNavHighlight } from '@paeffgen/react/crm-documents/ui';
import {
  FAVORITES_QUERY,
  FAVORITE_LABELS_QUERY,
  PINNED_FAVORITES_QUERY,
  ADD_FAVORITE_MUTATION,
  REMOVE_FAVORITE_MUTATION,
  ADD_FAVORITES_MUTATION,
  UPDATE_FAVORITE_MUTATION,
  ASSIGN_LABEL_MUTATION,
  REMOVE_LABEL_MUTATION,
  CREATE_FAVORITE_LABEL_MUTATION,
  RENAME_FAVORITE_LABEL_MUTATION,
  DELETE_FAVORITE_LABEL_MUTATION,
  UPDATE_FAVORITE_LABEL_COLOR_MUTATION,
  REORDER_FAVORITE_LABELS_MUTATION,
  type FavoriteItem,
  type FavoriteLabel,
} from '@paeffgen/react/crm-documents/data-access-graphql';

export interface UseFavoritesReturn {
  favorites: FavoriteItem[];
  /** Label-Namen, geordnet nach `sortOrder` (Backend). */
  userLabels: string[];
  /** Volle Label-Objekte (Name + persistierte Farbe + Reihenfolge). */
  labels: FavoriteLabel[];
  /** Name → persistierte Palette-Farbe (null = aus Name abgeleitet). */
  colorByLabel: Record<string, string | null>;
  loading: boolean;
  isFavorite: (articleNumber: string) => boolean;
  toggleFavorite: (articleNumber: string) => Promise<void>;
  updateFavorite: (
    articleNumber: string,
    updates: { pinned?: boolean; labels?: string[] }
  ) => Promise<void>;
  addFavorites: (articleNumbers: string[]) => Promise<void>;
  addUserLabel: (name: string, color?: string | null) => Promise<void>;
  deleteUserLabel: (name: string) => Promise<void>;
  renameUserLabel: (oldName: string, newName: string) => Promise<void>;
  recolorLabel: (name: string, color: string | null) => Promise<void>;
  reorderLabels: (orderedNames: string[]) => Promise<void>;
  assignLabel: (articleNumbers: string[], label: string) => Promise<void>;
  removeLabel: (articleNumbers: string[], label: string) => Promise<void>;
  toggleLabelOnArticle: (articleNumber: string, label: string) => void;
}

export function useFavorites(): UseFavoritesReturn {
  const { adminQueryVars, adminMutationVars } = useAdmin();
  const { markHighlight } = useNavHighlight();

  // Refetch lists must include adminQueryVars so the cache updates
  // for the correct customer context (not the admin's own data).
  const refetchFavorites = useMemo(
    () => [
      { query: FAVORITES_QUERY, variables: { ...adminQueryVars } },
      { query: PINNED_FAVORITES_QUERY, variables: { ...adminQueryVars } },
    ],
    [adminQueryVars]
  );
  const refetchLabels = useMemo(
    () => [{ query: FAVORITE_LABELS_QUERY, variables: { ...adminQueryVars } }],
    [adminQueryVars]
  );

  const { data: favoritesData, loading: favoritesLoading } =
    useQuery<{ favorites: FavoriteItem[] }>(FAVORITES_QUERY, {
      variables: { ...adminQueryVars },
    });
  const { data: labelsData, loading: labelsLoading } =
    useQuery<{ favoriteLabels: FavoriteLabel[] }>(FAVORITE_LABELS_QUERY, {
      variables: { ...adminQueryVars },
    });

  const [addFavoriteMut] = useMutation(ADD_FAVORITE_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [removeFavoriteMut] = useMutation(REMOVE_FAVORITE_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [addFavoritesMut] = useMutation(ADD_FAVORITES_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [updateFavoriteMut] = useMutation(UPDATE_FAVORITE_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [assignLabelMut] = useMutation(ASSIGN_LABEL_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [removeLabelMut] = useMutation(REMOVE_LABEL_MUTATION, {
    refetchQueries: refetchFavorites,
  });
  const [createLabelMut] = useMutation(CREATE_FAVORITE_LABEL_MUTATION, {
    refetchQueries: refetchLabels,
  });
  const [renameLabelMut] = useMutation(RENAME_FAVORITE_LABEL_MUTATION, {
    refetchQueries: [...refetchLabels, ...refetchFavorites],
  });
  const [recolorLabelMut] = useMutation(UPDATE_FAVORITE_LABEL_COLOR_MUTATION, {
    refetchQueries: refetchLabels,
  });
  const [reorderLabelsMut] = useMutation(REORDER_FAVORITE_LABELS_MUTATION, {
    refetchQueries: refetchLabels,
  });
  const [deleteLabelMut] = useMutation(DELETE_FAVORITE_LABEL_MUTATION, {
    refetchQueries: [...refetchLabels, ...refetchFavorites],
  });

  const favorites = favoritesData?.favorites ?? [];
  const labels = useMemo(
    () => labelsData?.favoriteLabels ?? [],
    [labelsData],
  );
  const userLabels = useMemo(() => labels.map((l) => l.name), [labels]);
  const colorByLabel = useMemo(
    () => Object.fromEntries(labels.map((l) => [l.name, l.color ?? null])),
    [labels],
  );

  const isFavorite = useCallback(
    (articleNumber: string) =>
      favorites.some((f) => f.articleNumber === articleNumber),
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (articleNumber: string) => {
      if (isFavorite(articleNumber)) {
        await removeFavoriteMut({ variables: { articleNumber, ...adminMutationVars } });
      } else {
        await addFavoriteMut({ variables: { articleNumber, ...adminMutationVars } });
        markHighlight('favoriten');
      }
    },
    [isFavorite, addFavoriteMut, removeFavoriteMut, adminMutationVars, markHighlight]
  );

  const updateFavorite = useCallback(
    async (
      articleNumber: string,
      updates: { pinned?: boolean; labels?: string[] }
    ) => {
      await updateFavoriteMut({
        variables: { articleNumber, ...updates, ...adminMutationVars },
      });
    },
    [updateFavoriteMut, adminMutationVars]
  );

  const addFavorites = useCallback(
    async (articleNumbers: string[]) => {
      if (articleNumbers.length === 0) return;
      await addFavoritesMut({ variables: { articleNumbers, ...adminMutationVars } });
      markHighlight('favoriten');
    },
    [addFavoritesMut, adminMutationVars, markHighlight]
  );

  const addUserLabel = useCallback(
    async (name: string, color?: string | null) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      await createLabelMut({ variables: { name: trimmed, color: color ?? null, ...adminMutationVars } });
    },
    [createLabelMut, adminMutationVars]
  );

  const deleteUserLabel = useCallback(
    async (name: string) => {
      await deleteLabelMut({ variables: { name, ...adminMutationVars } });
    },
    [deleteLabelMut, adminMutationVars]
  );

  const renameUserLabel = useCallback(
    async (oldName: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === oldName) return;
      await renameLabelMut({ variables: { oldName, newName: trimmed, ...adminMutationVars } });
    },
    [renameLabelMut, adminMutationVars]
  );

  const recolorLabel = useCallback(
    async (name: string, color: string | null) => {
      await recolorLabelMut({ variables: { name, color, ...adminMutationVars } });
    },
    [recolorLabelMut, adminMutationVars]
  );

  const reorderLabels = useCallback(
    async (orderedNames: string[]) => {
      await reorderLabelsMut({ variables: { orderedNames, ...adminMutationVars } });
    },
    [reorderLabelsMut, adminMutationVars]
  );

  const assignLabel = useCallback(
    async (articleNumbers: string[], label: string) => {
      await assignLabelMut({ variables: { articleNumbers, label, ...adminMutationVars } });
    },
    [assignLabelMut, adminMutationVars]
  );

  const removeLabel = useCallback(
    async (articleNumbers: string[], label: string) => {
      await removeLabelMut({ variables: { articleNumbers, label, ...adminMutationVars } });
    },
    [removeLabelMut, adminMutationVars]
  );

  const toggleLabelOnArticle = useCallback(
    (articleNumber: string, label: string) => {
      const fav = favorites.find((f) => f.articleNumber === articleNumber);
      if (!fav) return;
      const hasLabel = (fav.labels || []).includes(label);
      if (hasLabel) {
        removeLabelMut({
          variables: { articleNumbers: [articleNumber], label, ...adminMutationVars },
        });
      } else {
        assignLabelMut({
          variables: { articleNumbers: [articleNumber], label, ...adminMutationVars },
        });
      }
    },
    [favorites, assignLabelMut, removeLabelMut, adminMutationVars]
  );

  return {
    favorites,
    userLabels,
    labels,
    colorByLabel,
    loading: favoritesLoading || labelsLoading,
    isFavorite,
    toggleFavorite,
    updateFavorite,
    addFavorites,
    addUserLabel,
    deleteUserLabel,
    renameUserLabel,
    recolorLabel,
    reorderLabels,
    assignLabel,
    removeLabel,
    toggleLabelOnArticle,
  };
}
